import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Table, TableHead, TableHeaderCell, TableBody, TableRow, TableCell } from '../ui/table';
import TagPill from '../ui/TagPill';

export interface BlueprintComparisonData {
    promptId: string;
    promptName: string;
    totalImpressions: number;
    brandSafeUrls: number;
    matchRate: number;
    dimensions: Record<string, Array<{ value: string; impressions: number; percentage: number }>>;
}

export interface BlueprintComparisonProps {
    data: BlueprintComparisonData[];
    availableBlueprints: Array<{ id: string; name: string }>;
    selectedBlueprintIds: string[];
    onAddBlueprint: (id: string) => void;
    onRemoveBlueprint: (id: string) => void;
    loading?: boolean;
}

function formatNumber(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toLocaleString();
}

function getHighlightClass(value: number, allValues: number[]): string {
    const max = Math.max(...allValues);
    const min = Math.min(...allValues.filter((v) => v > 0));
    if (allValues.length < 2 || max === min) return '';
    if (value === max && max >= min * 2) return 'bg-safe-bg text-safe';
    if (value === min && max >= min * 2) return 'bg-warn-bg text-warn';
    return '';
}

const BlueprintComparison: React.FC<BlueprintComparisonProps> = ({
    data,
    availableBlueprints,
    selectedBlueprintIds,
    onAddBlueprint,
    onRemoveBlueprint,
    loading = false,
}) => {
    return (
        <Card>
            <CardHeader>
                <CardTitle>Blueprint Comparison</CardTitle>
            </CardHeader>
            <CardContent>
                <BlueprintSelector
                    availableBlueprints={availableBlueprints}
                    selectedBlueprintIds={selectedBlueprintIds}
                    onAdd={onAddBlueprint}
                    onRemove={onRemoveBlueprint}
                />
                <ComparisonBody data={data} loading={loading} />
            </CardContent>
        </Card>
    );
};

export default BlueprintComparison;

function BlueprintSelector({
    availableBlueprints,
    selectedBlueprintIds,
    onAdd,
    onRemove,
}: {
    availableBlueprints: Array<{ id: string; name: string }>;
    selectedBlueprintIds: string[];
    onAdd: (id: string) => void;
    onRemove: (id: string) => void;
}) {
    const unselected = availableBlueprints.filter((b) => !selectedBlueprintIds.includes(b.id));
    const canAdd = selectedBlueprintIds.length < 3 && unselected.length > 0;

    return (
        <div className="flex flex-wrap items-center gap-2 mb-6">
            {selectedBlueprintIds.map((id) => {
                const bp = availableBlueprints.find((b) => b.id === id);
                return (
                    <SelectedChip key={id} name={bp?.name ?? id} onRemove={() => onRemove(id)} />
                );
            })}
            {canAdd && <AddDropdown options={unselected} onSelect={onAdd} />}
        </div>
    );
}

function SelectedChip({ name, onRemove }: { name: string; onRemove: () => void }) {
    return (
        <TagPill onRemove={onRemove} removeLabel={`Remove ${name}`}>
            {name}
        </TagPill>
    );
}

function AddDropdown({
    options,
    onSelect,
}: {
    options: Array<{ id: string; name: string }>;
    onSelect: (id: string) => void;
}) {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        }
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-line px-3 py-1 text-sm font-medium text-ink-muted hover:bg-[var(--ghost-bg)]"
            >
                Add Blueprint
            </button>
            {open && (
                <DropdownMenu
                    options={options}
                    onSelect={(id) => {
                        onSelect(id);
                        setOpen(false);
                    }}
                />
            )}
        </div>
    );
}

function DropdownMenu({
    options,
    onSelect,
}: {
    options: Array<{ id: string; name: string }>;
    onSelect: (id: string) => void;
}) {
    return (
        <ul className="absolute z-10 mt-1 w-56 rounded-md border border-line bg-surface py-1 shadow-lg">
            {options.map((opt) => (
                <li key={opt.id}>
                    <button
                        className="w-full px-4 py-2 text-left text-sm hover:bg-[var(--ghost-bg)]"
                        onClick={() => onSelect(opt.id)}
                    >
                        {opt.name}
                    </button>
                </li>
            ))}
        </ul>
    );
}

function ComparisonBody({ data, loading }: { data: BlueprintComparisonData[]; loading: boolean }) {
    if (loading) return <SkeletonTable columns={3} />;
    if (data.length < 2) {
        return (
            <p className="text-sm text-ink-muted py-4 text-center">
                Select at least 2 blueprints to compare
            </p>
        );
    }
    return <ComparisonTable data={data} />;
}

function SkeletonTable({ columns }: { columns: number }) {
    return (
        <div className="space-y-3 animate-pulse">
            {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex gap-4">
                    <div className="h-5 w-32 rounded bg-line" />
                    {Array.from({ length: columns }).map((_, j) => (
                        <div key={j} className="h-5 flex-1 rounded bg-canvas" />
                    ))}
                </div>
            ))}
        </div>
    );
}

function ComparisonTable({ data }: { data: BlueprintComparisonData[] }) {
    const dimensionKeys = collectDimensionKeys(data);

    return (
        <div className="overflow-x-auto">
            <Table>
                <TableHead>
                    <tr>
                        <TableHeaderCell>Metric</TableHeaderCell>
                        {data.map((bp) => (
                            <TableHeaderCell key={bp.promptId} className="text-right">
                                {bp.promptName}
                            </TableHeaderCell>
                        ))}
                    </tr>
                </TableHead>
                <TableBody>
                    <MetricRow label="Total Impressions" values={data.map((d) => d.totalImpressions)} format={formatNumber} />
                    <MetricRow label="Brand-Safe URLs" values={data.map((d) => d.brandSafeUrls)} format={(n) => n.toLocaleString()} />
                    <MetricRow label="Match Rate" values={data.map((d) => d.matchRate)} format={(n) => `${n.toFixed(1)}%`} />
                    {dimensionKeys.map((key) => (
                        <DimensionRows key={key} dimensionKey={key} data={data} />
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

function collectDimensionKeys(data: BlueprintComparisonData[]): string[] {
    const keys = new Set<string>();
    data.forEach((d) => Object.keys(d.dimensions).forEach((k) => keys.add(k)));
    return Array.from(keys);
}

function MetricRow({
    label,
    values,
    format,
}: {
    label: string;
    values: number[];
    format: (n: number) => string;
}) {
    return (
        <TableRow>
            <TableCell className="font-medium">{label}</TableCell>
            {values.map((v, i) => (
                <TableCell key={i} className={`text-right tabular-nums ${getHighlightClass(v, values)}`}>
                    {format(v)}
                </TableCell>
            ))}
        </TableRow>
    );
}

function DimensionRows({
    dimensionKey,
    data,
}: {
    dimensionKey: string;
    data: BlueprintComparisonData[];
}) {
    const label = dimensionKey.charAt(0).toUpperCase() + dimensionKey.slice(1);
    const topValues = collectTopValues(dimensionKey, data);

    return (
        <>
            <tr>
                <td colSpan={data.length + 1} className="pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-ink-muted">
                    {label} (Top 3)
                </td>
            </tr>
            {topValues.map((row) => (
                <DimensionValueRow key={row.value} row={row} count={data.length} />
            ))}
        </>
    );
}

interface TopValueRow {
    value: string;
    cells: Array<{ impressions: number; percentage: number } | null>;
}

function collectTopValues(dimensionKey: string, data: BlueprintComparisonData[]): TopValueRow[] {
    const allValues = new Set<string>();
    data.forEach((d) => {
        (d.dimensions[dimensionKey] ?? []).forEach((item) => allValues.add(item.value));
    });

    const rows: TopValueRow[] = Array.from(allValues).map((value) => ({
        value,
        cells: data.map((d) => {
            const item = (d.dimensions[dimensionKey] ?? []).find((x) => x.value === value);
            return item ? { impressions: item.impressions, percentage: item.percentage } : null;
        }),
    }));

    rows.sort((a, b) => {
        const sumA = a.cells.reduce((s, c) => s + (c?.impressions ?? 0), 0);
        const sumB = b.cells.reduce((s, c) => s + (c?.impressions ?? 0), 0);
        return sumB - sumA;
    });

    return rows.slice(0, 3);
}

function DimensionValueRow({ row, count }: { row: TopValueRow; count: number }) {
    const impressions = row.cells.map((c) => c?.impressions ?? 0);

    return (
        <TableRow>
            <TableCell className="py-1.5 pl-4 text-ink-muted">{row.value}</TableCell>
            {row.cells.map((cell, i) => (
                <TableCell key={i} className={`py-1.5 text-right tabular-nums ${getHighlightClass(impressions[i], impressions)}`}>
                    {cell ? `${formatNumber(cell.impressions)} (${cell.percentage.toFixed(1)}%)` : '—'}
                </TableCell>
            ))}
        </TableRow>
    );
}
